/**
 * THE ONE PLACE THAT TALKS TO THE MANAGER.
 *
 * Every request goes out from here, which is what makes two rules cheap to
 * keep: the token is attached in exactly one function, and a 401 clears it in
 * exactly one function. Scattering `fetch` through the pages means the sixth
 * one forgets the header, and a stale token then produces a page of empty
 * lists instead of a sign-in screen.
 *
 * PATHS ARE ABSOLUTE AND HOSTLESS — `/projects`, never
 * `https://api…/projects`. In production the SPA is served by the manager
 * itself under `/admin`, so same-origin is a fact rather than a configuration;
 * in dev Vite proxies the same paths through (`vite.config.ts`). There is no
 * API base URL to get wrong, and no CORS preflight on the hot path.
 *
 * FAILURES ARE `ApiFailure`, ALWAYS, including the ones the network caused.
 * The server sends `{error, code}` for every failure it knows about
 * (`apps/server/src/http/errors.ts`); a dropped connection sends nothing at
 * all. Both arrive here as the same class so a caller has one thing to catch.
 */

const TOKEN_KEY = 'taginai.admin.token';

/* ------------------------------------------------------------------ */
/* The token                                                           */
/* ------------------------------------------------------------------ */

/**
 * localStorage and not a cookie. The manager issues an OPAQUE BEARER token and
 * expects it in an `Authorization` header (`auth/tokens.ts`); it sets no
 * cookie and reads none, so there is nothing for a browser to send
 * automatically. A header the page attaches by hand is also immune to CSRF,
 * which a cookie on the same origin as the API would not be.
 */
export function loadToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return null;
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* A session that cannot be remembered still works until the tab closes. */
  }
}

/* ------------------------------------------------------------------ */
/* Failure                                                             */
/* ------------------------------------------------------------------ */

export type ApiCode =
  | 'invalid'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'server_error'
  /** Not from the server: the request never arrived or never came back. */
  | 'network';

export class ApiFailure extends Error {
  readonly status: number;
  readonly code: ApiCode;

  constructor(status: number, code: ApiCode, message: string) {
    super(message);
    this.name = 'ApiFailure';
    this.status = status;
    this.code = code;
  }
}

/** True for the two failures a page should answer by showing sign-in again. */
export function isSignedOut(err: unknown): boolean {
  return err instanceof ApiFailure && err.status === 401;
}

/* ------------------------------------------------------------------ */
/* The request                                                         */
/* ------------------------------------------------------------------ */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Omitted for `/auth/login`, which is the request that GETS the token. */
  token?: string | null;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token, signal } = options;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiFailure(0, 'network', 'Could not reach the server.');
  }

  // 204, and any other body-less success. `res.json()` on an empty body throws
  // a SyntaxError that says nothing about what actually happened.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!res.ok) {
    const shape = parsed as { error?: unknown; code?: unknown } | undefined;
    const message = typeof shape?.error === 'string' ? shape.error : `Request failed (${res.status}).`;
    const code = typeof shape?.code === 'string' ? (shape.code as ApiCode) : 'server_error';
    /*
     * A 401 means this token is finished — expired, revoked, or from a
     * database that has since been reseeded. Dropping it here rather than at
     * each call site is what stops a dead token surviving a page reload and
     * producing a signed-in-looking shell with nothing in it.
     */
    if (res.status === 401) storeToken(null);
    throw new ApiFailure(res.status, code, message);
  }

  return parsed as T;
}

/* ------------------------------------------------------------------ */
/* The shapes this page uses                                           */
/* ------------------------------------------------------------------ */

/*
 * DECLARED HERE, NOT IMPORTED FROM `@lableforge/core`.
 *
 * The phone imports the wire types from core so a change to the contract is a
 * build error on both sides at once, and that is right for the app the whole
 * product depends on. This page is a different bet: it is an internal tool
 * that must keep building even while core is being edited by somebody else,
 * and every shape below is one the server already sends today. Nothing here
 * is a type core owns — `AdminEngine` and `AdminRecording` are this page's
 * own vocabulary, defined by `routes/admin.ts` and read by nothing else.
 */

export interface Me {
  id: string;
  displayName: string;
  email: string | null;
  status: 'active' | 'suspended';
}

export interface Project {
  id: string;
  name: string;
  code: string;
  language: string;
  role: 'admin' | 'reviewer' | 'labeler';
  /** `transcribe` receives uploads; `record` produces audio in-house. */
  kind: string;
  description: string | null;
  recordingCount: number;
}

export interface Engine {
  id: string;
  label: string;
  /** What gets stamped on `recordings.engine` when this one runs. */
  chain: string;
}

export type RecordingState = 'uploaded' | 'queued' | 'processing' | 'review' | 'done' | 'failed';

/** A named pipeline template: a name over a chain string. */
export interface Pipeline {
  id: string;
  name: string;
  chain: string;
}

export interface AdminRecording {
  id: string;
  name: string;
  description: string | null;
  /** Set when a production task produced it — the "task" badge. */
  sourceTaskId: string | null;
  status: RecordingState;
  durationSec: number;
  sizeBytes: number | null;
  language: string;
  engine: string | null;
  error: string | null;
  segmentCount: number;
  /** Every arrangement this recording holds — machine passes and people's. */
  versionCount: number;
  /** The human path: 'direct', 'anchored' (silence-first), 'staged' (the
   *  ladder — silences, noises, umms, unclear, then words) or 'extract'
   *  (pull each kind out of the unknown pool, then sort what is left). */
  flow: 'direct' | 'anchored' | 'staged' | 'extract' | 'sshwl';
  /** Anchored flow: when the silence stage was settled; null while waiting. */
  anchorsSettledAt: string | null;
  /** Staged flow: the rung being walked ('silence'…'word', or 'done'). */
  stage: string | null;
  createdAt: string;
  processedAt: string | null;
}

export interface AdminTask {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  description: string | null;
  script: string | null;
  /** 1 = solo (record yourself), 2 = duo (a Talk conversation). */
  peopleRequired: number;
  createdAt: string;
  producedCount: number;
}

export interface CreatedRecording {
  recordingId: string;
  uploadUrl: string;
  storageKey: string;
}

/**
 * A signed GET of the ORIGINAL upload, for the player.
 *
 * `expiresAt` is read here rather than ignored: this URL is held by an `<audio>`
 * element that keeps returning to it for as long as somebody is listening, so
 * the page re-asks before a seek lands on a dead signature. See
 * `AdminRecordingAudio` in `packages/core/src/adminApi.ts` for the contract.
 */
export interface RecordingAudio {
  url: string;
  expiresAt: string;
}

/* ---- People ------------------------------------------------------- */

export type UserStatus = 'active' | 'disabled' | 'suspended';
export type Role = 'admin' | 'reviewer' | 'labeler';

/** Owner-assigned trust. Displayed and set at creation; enforced nowhere yet
 *  — settlement quotas read it later. */
export type TrustLevel = 'low' | 'medium' | 'high' | 'super';

export interface AdminUser {
  id: string;
  email: string | null;
  displayName: string;
  role: Role;
  status: UserStatus;
  /** Absent from servers older than this field; read as 'low'. */
  trust?: TrustLevel;
  createdAt: string;
}

/** Create and reset both answer with a password that exists nowhere else. */
export interface OneTimePassword {
  user: AdminUser;
  tempPassword: string;
  /** Only on a reset: how many devices were signed out by it. */
  sessionsRevoked?: number;
}

/** Create alone can also answer with NO password: the email already had an
 *  account, so the person was simply added to the project and nothing secret
 *  exists to show. */
export interface CreatedOrAdded {
  user: AdminUser;
  tempPassword: string | null;
}

export interface UserActionResult {
  user: AdminUser;
  note: string;
}

/* ---- Versions ----------------------------------------------------- */

export interface Version {
  id: string;
  recordingId: string;
  label: string;
  authorName: string | null;
  status: 'open' | 'main' | 'archived';
  votes: number;
  isMine: boolean;
  isMyPick: boolean;
  segmentCount: number;
  /** The author's own progress through it — settled clips out of
   *  `segmentCount`. Null on the machine's pass; absent on older servers. */
  judgedCount?: number | null;
  /**
   * WHEN ITS AUTHOR HANDED IT OVER — the one field on this list that means
   * "somebody is waiting for you". `TimelineVersion.submittedAt` in
   * `packages/core/src/api.ts` is the contract; a submitted version that is not
   * `main` is exactly a recording waiting for review, and that is what this
   * page sorts and badges by.
   *
   * OPTIONAL, AND THAT IS LOAD-BEARING RATHER THAN LAZY. The stamp landed in
   * the contract before it landed in the server's responses, so every payload
   * in production today comes back WITHOUT this key. Optional means the badge
   * helpers in `review.ts` answer `null` and every surface renders nothing —
   * not "submitted undefined", not an empty pill. When the server starts
   * sending it, the same code starts showing it, with no second deploy here.
   */
  submittedAt?: string | null;
  createdAt: string;
}

export interface VersionList {
  recordingId: string;
  recordingName: string;
  language: string;
  durationSec: number;
  versions: Version[];
}

/** One row of one arrangement. The subset this page draws — the wire carries
 *  more, and reading only what is used keeps a server-side addition from
 *  being a compile error here. */
export interface VersionRow {
  id: string;
  index: number;
  startSec: number;
  endSec: number;
  heard: string;
  contextual: string | null;
  kind: string;
  /** Which speaker said it, within THIS recording. Null when the engine that
   *  made the row does not separate speakers. */
  speaker: string | null;
  confidence: number | null;
  verify: string;
  clipUrl: string;
  /** The aligner's trust in WHERE the words sit. Absent — not null — when the
   *  pipeline never scored it or an edit invalidated the score. */
  alignScore?: number;
}

export interface VersionDetail {
  version: Version;
  recordingName: string;
  language: string;
  durationSec: number;
  segments: VersionRow[];
}

export interface PromoteResult {
  versions: Version[];
  promoted: boolean;
  note: string;
}

/* ------------------------------------------------------------------ */
/* The calls                                                           */
/* ------------------------------------------------------------------ */

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; expiresAt: string }>('/auth/login', {
      method: 'POST',
      body: { email, password },
    }),

  logout: (token: string) => request<void>('/auth/logout', { method: 'POST', token }),

  me: (token: string) => request<{ user: Me }>('/me', { token }),

  projects: (token: string) => request<{ projects: Project[] }>('/projects', { token }),

  /** The first step of everything: a project, named and typed. */
  createProject: (
    token: string,
    input: { name: string; kind: string; description?: string },
  ) =>
    request<{ ok: boolean; projectId: string }>('/projects', {
      method: 'POST',
      token,
      body: input,
    }),

  /** Edit a project — name, description or kind. */
  patchProject: (
    token: string,
    projectId: string,
    patch: Partial<{ name: string; description: string; kind: string }>,
  ) => request<{ ok: boolean }>(`/projects/${projectId}`, { method: 'PATCH', token, body: patch }),

  /* ---- Production tasks ------------------------------------------- */

  createTask: (
    token: string,
    projectId: string,
    input: { title: string; description?: string; script?: string; peopleRequired: number },
  ) =>
    request<{ ok: boolean; taskId: string }>(`/projects/${projectId}/tasks`, {
      method: 'POST',
      token,
      body: input,
    }),

  listTasks: (token: string, projectId: string, signal?: AbortSignal) =>
    request<{ tasks: AdminTask[] }>(`/projects/${projectId}/tasks`, { token, signal }),

  patchTask: (
    token: string,
    taskId: string,
    patch: Partial<{ title: string; description: string; script: string; archived: boolean }>,
  ) => request<{ ok: boolean }>(`/tasks/${taskId}`, { method: 'PATCH', token, body: patch }),

  /** Rename or describe one recording — the card's Edit. */
  patchRecording: (
    token: string,
    recordingId: string,
    patch: Partial<{
      name: string;
      description: string;
      flow: 'direct' | 'anchored' | 'staged' | 'extract' | 'sshwl';
    }>,
  ) =>
    request<{ ok: boolean }>(`/admin/recordings/${recordingId}`, {
      method: 'PATCH',
      token,
      body: patch,
    }),

  engines: (token: string) => request<{ engines: Engine[] }>('/admin/engines', { token }),

  recordings: (token: string, projectId: string, signal?: AbortSignal) =>
    request<{ recordings: AdminRecording[] }>(
      `/admin/recordings?projectId=${encodeURIComponent(projectId)}`,
      { token, ...(signal ? { signal } : {}) },
    ),

  createRecording: (
    token: string,
    input: {
      projectId: string;
      name: string;
      durationSec: number;
      sizeBytes: number;
      language?: string;
      /** The human path: omitted = 'direct'; 'anchored' = silence-first. */
      flow?: 'direct' | 'anchored';
    },
  ) => request<CreatedRecording>('/admin/recordings', { method: 'POST', token, body: input }),

  /**
   * The owner's split: uploading parks a recording; THIS runs a pass, and
   * every run names a pipeline template — no fallback, no champion. Each run
   * lands as its own version, labeled with the pipeline's name.
   */
  runRecording: (token: string, recordingId: string, pipelineId: string) =>
    request<{ ok: boolean; status: string; engine: string; pipeline: string }>(
      `/admin/recordings/${recordingId}/run`,
      { method: 'POST', token, body: { pipelineId } },
    ),

  /* ---- pipeline templates ---- */
  listPipelines: (token: string, signal?: AbortSignal) =>
    request<{ pipelines: Pipeline[] }>('/admin/pipelines', {
      token,
      ...(signal ? { signal } : {}),
    }),

  createPipeline: (token: string, input: { name: string; chain: string }) =>
    request<{ ok: boolean; id: string }>('/admin/pipelines', {
      method: 'POST',
      token,
      body: input,
    }),

  /** Rename a pipeline or change its chain — future runs only; past runs
   *  keep the name and chain they were started under. */
  patchPipeline: (token: string, pipelineId: string, patch: Partial<{ name: string; chain: string }>) =>
    request<{ ok: boolean }>(`/admin/pipelines/${pipelineId}`, {
      method: 'PATCH',
      token,
      body: patch,
    }),

  deletePipeline: (token: string, pipelineId: string) =>
    request<{ ok: boolean }>(`/admin/pipelines/${pipelineId}`, {
      method: 'DELETE',
      token,
      body: { confirm: 'delete' },
    }),

  /**
   * PERMANENTLY delete a recording — audio, every version, every verdict, for
   * everyone. The server refuses unless `confirmName` is the recording's exact
   * name: the typed confirmation is enforced where no UI can skip it.
   */
  deleteRecording: (token: string, recordingId: string, confirmName: string) =>
    request<{ ok: boolean }>(`/admin/recordings/${recordingId}`, {
      method: 'DELETE',
      token,
      body: { confirmName },
    }),

  /** The whole project — everything under it and the project itself. */
  deleteProject: (token: string, projectId: string, confirmName: string) =>
    request<{ ok: boolean; recordings: number }>(`/admin/projects/${projectId}`, {
      method: 'DELETE',
      token,
      body: { confirmName },
    }),

  /** The clean slate: empty EVERY project. Accounts and projects survive. */
  wipeAllData: (token: string, confirm: string) =>
    request<{ ok: boolean; recordings: number }>('/admin/data/wipe', {
      method: 'POST',
      token,
      body: { confirm },
    }),

  /** The account and the work it did, everywhere. Never yourself. */
  deleteUser: (token: string, userId: string, confirmEmail: string) =>
    request<{ ok: boolean }>(`/admin/users/${userId}`, {
      method: 'DELETE',
      token,
      body: { confirmEmail },
    }),

  /**
   * The whole recording, signed. Fifteen minutes (`ORIGINAL_AUDIO_TTL_SEC`),
   * because the player seeks around it for as long as the page is open.
   */
  recordingAudio: (token: string, recordingId: string, signal?: AbortSignal) =>
    request<RecordingAudio>(`/admin/recordings/${recordingId}/audio`, {
      token,
      ...(signal ? { signal } : {}),
    }),

  /*
   * THE SHARED CONFIRM, not an admin copy of it. Step 3 of the upload is
   * identical whoever uploaded — it flips `uploaded` to `queued` and is
   * idempotent for the retry a dropped connection forces. An admin is a
   * project member, so the phone's own endpoint already accepts this call.
   */
  confirmUpload: (token: string, recordingId: string, opts?: { queue?: boolean }) =>
    request<unknown>(`/recordings/${recordingId}/confirm`, {
      method: 'POST',
      token,
      // `queue: false` acknowledges the bytes without starting the pipeline —
      // the admin runs the first pass as its own step, engine chosen then.
      body: { recordingId, ...(opts?.queue === false ? { queue: false } : {}) },
    }),

  /* ---- People ----------------------------------------------------- */

  /** The one list: every account. Users are people, not seats. */
  users: (token: string, signal?: AbortSignal) =>
    request<{ users: AdminUser[] }>('/admin/users', { token, ...(signal ? { signal } : {}) }),

  createUser: (
    token: string,
    input: {
      email: string;
      displayName: string;
      /** GLOBAL role. Omitted = labeler. */
      role?: 'admin' | 'labeler';
      /** Omitted = 'low'. */
      trust?: TrustLevel;
    },
  ) => request<CreatedOrAdded>('/admin/users', { method: 'POST', token, body: input }),

  /*
   * `projectId` GOES IN THE BODY of all three, even though the server treats it
   * as optional. It decides which seat's role comes back for somebody who is on
   * two of this administrator's projects, and the page always knows which one it
   * is looking at — so answering the question is free here and a guess there.
   */
  disableUser: (token: string, userId: string) =>
    request<UserActionResult>(`/admin/users/${userId}/disable`, {
      method: 'POST',
      token,
      body: {},
    }),

  enableUser: (token: string, userId: string) =>
    request<UserActionResult>(`/admin/users/${userId}/enable`, {
      method: 'POST',
      token,
      body: {},
    }),

  resetPassword: (token: string, userId: string) =>
    request<OneTimePassword>(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      token,
      body: {},
    }),

  /** Change a person's trust. Instant; touches nothing else. */
  setTrust: (token: string, userId: string, trust: TrustLevel) =>
    request<UserActionResult>(`/admin/users/${userId}/trust`, {
      method: 'POST',
      token,
      body: { trust },
    }),

  /** Replace YOUR OWN password. The session making the call stays signed in;
   *  every other one is revoked. */
  changePassword: (token: string, current: string, next: string) =>
    request<{ ok: boolean; otherSessionsRevoked: number }>('/auth/password', {
      method: 'POST',
      token,
      body: { current, next },
    }),

  /* ---- Versions --------------------------------------------------- */

  versions: (token: string, recordingId: string, signal?: AbortSignal) =>
    request<VersionList>(`/versions?recordingId=${encodeURIComponent(recordingId)}`, {
      token,
      ...(signal ? { signal } : {}),
    }),

  versionDetail: (token: string, versionId: string, signal?: AbortSignal) =>
    request<VersionDetail>(`/versions/${versionId}`, { token, ...(signal ? { signal } : {}) }),

  /** The administrator's override. Moves `main` with no quorum — see the route
   *  in `apps/server/src/routes/admin.ts` for why that is a separate door. */
  promoteVersion: (token: string, versionId: string) =>
    request<PromoteResult>(`/admin/versions/${versionId}/promote`, { method: 'POST', token }),

  /**
   * PERMANENTLY delete one version — its clips, verdicts and votes, for
   * everyone. If it was the truth so far, the oldest survivor takes over; if
   * nothing survives, the recording returns to `uploaded`.
   */
  deleteVersion: (token: string, versionId: string) =>
    request<{ ok: boolean }>(`/admin/versions/${versionId}`, {
      method: 'DELETE',
      token,
      body: { confirm: 'delete' },
    }),
};

/* ------------------------------------------------------------------ */
/* The one request that is not `fetch`                                 */
/* ------------------------------------------------------------------ */

/**
 * PUT the bytes straight to object storage, with a progress callback.
 *
 * XMLHttpRequest, in 2026, for one reason: `fetch` cannot report UPLOAD
 * progress. It can stream a request body in Chrome behind a flag and nowhere
 * else, and an admin pushing a 200 MB session file needs to see a number move
 * — a spinner that sits still for four minutes is indistinguishable from a
 * hang, and the honest guess is then to cancel and retry, which doubles the
 * upload. XHR's `upload.onprogress` has done this since 2008.
 *
 * The URL is presigned and short-lived, and it is the ONLY request this page
 * makes that does not go to the manager. No `Authorization` header goes with
 * it: the signature IS the authorisation, and sending our bearer token to a
 * storage host would hand it to a third party.
 */
export function putBytes(
  url: string,
  file: File,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
        return;
      }
      /*
       * The storage host answers in XML, not our `{error, code}`. Its status
       * is the only part worth showing: `403` here almost always means the
       * signature expired, which is a real and recoverable thing to say.
       */
      reject(new ApiFailure(xhr.status, 'server_error', `Upload rejected by storage (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new ApiFailure(0, 'network', 'The upload connection failed.'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled.', 'AbortError'));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}
